import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import { fetchDeepLink, openDeepLink } from '../deeplinks';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Button,
  Tooltip,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  IconButton,
  Popover,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { DataGridPro } from '@mui/x-data-grid-pro';
import '../App.css';
import { TRACKING_API_URL } from '../config';
// ══════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════

const PREFERRED_BOOK_ORDER = [
  'DraftKings', 'Pinnacle', 'Bookmaker', 'Circa', 'FanDuel', 'Bet365',
  'theScore Bet', 'Caesars', 'BetMGM', 'BetRivers', 'Bovada', 'Hard Rock',
  'Fanatics', 'Prophet Exchange', 'NoVig', 'Polymarket', 'Kalshi', 'LowVig',
];

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

// ══════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════

const EMPTY_ARRAY = [];

const renderFilterValue = (sel, opts, label) => {
  if (!sel.length) return label;
  if (sel.length === opts.length) return 'All';
  return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
};

const FilterSelect = ({ id, label, value, options, onChange, disabled }) => (
  <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
    <InputLabel id={id} sx={{ color: '#ffffff' }}>{label}</InputLabel>
    <Select multiple labelId={id} label={label} notched value={value} onChange={onChange} renderValue={sel => renderFilterValue(sel, options, label)} disabled={disabled}>
      <MenuItem value="all"><Checkbox checked={value.length === options.length} /><ListItemText primary="Select All" /></MenuItem>
      {options.map(o => <MenuItem key={o} value={o}><Checkbox checked={value.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
    </Select>
  </FormControl>
);

const capitalizeSide = (side) => {
  if (!side) return '';
  return side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
};

const formatLine = (side, points) => {
  const sideAbbr = side === 'Over' ? 'o' : side === 'Under' ? 'u' : '';
  return `${sideAbbr}${points ?? ''}`.trim();
};

// ══════════════════════════════════════════════════════════
//  CELL COMPONENTS
// ══════════════════════════════════════════════════════════

const DeeplinkCell = React.memo(({ oddsId, hasDeeplink, isAuthenticated }) => {
  const disabled = !hasDeeplink || !isAuthenticated;
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Button
        onClick={async () => { if (hasDeeplink) { const url = await fetchDeepLink(oddsId); if (url) openDeepLink(url); } }}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        Bet
      </Button>
    </Box>
  );
});

const HideMiddleCell = React.memo(({ row, hiddenMiddles, toggleHide, showHiddenMiddles, isAuthenticated }) => {
  const key = row.middle_key;
  const isHidden = hiddenMiddles.includes(key);
  const isDisabled = !isAuthenticated;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => { if (!isDisabled) toggleHide(row); }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenMiddles) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

const TrackMiddleCell = React.memo(({ row, trackedBets, toggleTrack, trackingInProgress, isAuthenticated }) => {
  const bet1Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === row.oddsId1 || bet.odds_id === row.oddsId1);
  const bet2Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === row.oddsId2 || bet.odds_id === row.oddsId2);
  const isFullyTracked = bet1Tracked && bet2Tracked;
  const isDisabled = trackingInProgress.has(row.middle_key) || !isAuthenticated;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => { if (!isDisabled) toggleTrack(row, bet1Tracked, bet2Tracked); }}
        size="small"
        sx={{ padding: 0, color: isFullyTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isFullyTracked ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
        )}
      </IconButton>
    </Box>
  );
});

// ══════════════════════════════════════════════════════════
//  MOBILE CARD
// ══════════════════════════════════════════════════════════

const MobileMiddleCard = React.memo(({
  middle, trackedBets, toggleTrack, trackingInProgress, isAuthenticated,
  hiddenMiddles, toggleHide, showHiddenMiddles
}) => {
  const oddsFormat = useOddsFormat();
  const bet1Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === middle.oddsId1 || bet.odds_id === middle.oddsId1);
  const bet2Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === middle.oddsId2 || bet.odds_id === middle.oddsId2);
  const isTracked = bet1Tracked && bet2Tracked;
  const isTrackingDisabled = trackingInProgress.has(middle.middle_key) || !isAuthenticated;
  const isHidden = hiddenMiddles.includes(middle.middle_key);
  const isHideDisabled = (isHidden && !showHiddenMiddles) || !isAuthenticated;

  const eventDate = middle.eventStart
    ? new Date(middle.eventStart).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')
    : '';

  const line1 = formatLine(middle.side1, middle.points1);
  const line2 = formatLine(middle.side2, middle.points2);

  return (
    <Card sx={{ mb: 2, border: '1px solid #333', backgroundColor: '#1e1e1e', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
      <Box sx={{ backgroundColor: '#2a2a2a', px: 2, py: 1.5, borderBottom: '1px solid #333' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#ffffff' }}>Middle Opportunity</Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => {
                if (!isTrackingDisabled) toggleTrack(middle, bet1Tracked, bet2Tracked);
              }}
              disabled={isTrackingDisabled}
              sx={{ color: isTracked ? '#00ff00' : '#888', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, '&.Mui-disabled': { color: '#444' } }}
            >
              {isTracked ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => !isHideDisabled && toggleHide(middle)}
              disabled={isHideDisabled}
              sx={{ color: isHidden ? '#00ff00' : '#888', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, '&.Mui-disabled': { color: '#444' } }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {(middle.playerName || middle.betTypeName) && (
          <Box mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>{middle.playerName}</Typography>
            <Typography variant="body2" sx={{ color: '#BBBBBB' }}>{middle.betTypeName}</Typography>
          </Box>
        )}

        <Box mb={2}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            {middle.league && (<><Typography variant="body2" sx={{ color: '#ffffff' }}>{middle.league}</Typography><Typography variant="body2" sx={{ color: '#ffffff' }}>|</Typography></>)}
            {middle.awayLogo && <img src={middle.awayLogo} alt={middle.awayAbbr} style={{ width: 'auto', height: '16px', objectFit: 'contain' }} loading="lazy" />}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>{middle.awayAbbr || middle.awayTeam}</Typography>
            <Typography variant="body2" sx={{ mx: 0.5, color: '#ffffff' }}>@</Typography>
            {middle.homeLogo && <img src={middle.homeLogo} alt={middle.homeAbbr} style={{ width: 'auto', height: '16px', objectFit: 'contain' }} loading="lazy" />}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>{middle.homeAbbr || middle.homeTeam}</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#BBBBBB' }}>{eventDate}</Typography>
        </Box>

        <Stack spacing={2}>
          {[1, 2].map(side => {
            const logo = side === 1 ? middle.bookLogo1 : middle.bookLogo2;
            const bookName = side === 1 ? middle.bookName1 : middle.bookName2;
            const deepLink = side === 1 ? middle.hasDeeplink1 : middle.hasDeeplink2;
            const oddsId = side === 1 ? middle.oddsId1 : middle.oddsId2;
            const line = side === 1 ? line1 : line2;
            const price = side === 1 ? middle.price1 : middle.price2;
            const roi = side === 1 ? middle.roi1 : middle.roi2;
            return (
              <Box key={side} sx={{ p: 1.5, borderRadius: 1, backgroundColor: '#2a2a2a', border: '1px solid #444' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Tooltip title={bookName || ''} placement="top" arrow>
                    <img src={logo} alt={bookName} style={{ width: 60, height: 30, objectFit: 'contain' }} loading="lazy" />
                  </Tooltip>
                  <Button
                    variant="contained" size="small"
                    onClick={async () => { if (deepLink) { const url = await fetchDeepLink(oddsId); if (url) openDeepLink(url); } }}
                    disabled={!deepLink || !isAuthenticated}
                    sx={{
                      backgroundColor: deepLink && isAuthenticated ? '#00ff00' : '#333',
                      color: deepLink && isAuthenticated ? '#000' : '#666',
                      minWidth: 70,
                      '&:hover': { backgroundColor: deepLink && isAuthenticated ? '#00cc00' : '#333' },
                      '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                    }}
                  >
                    Bet
                  </Button>
                </Stack>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
                  {line} {fmtPrice(price, oddsFormat)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#00ff00' }}>EV: {roi.toFixed(1)}%</Typography>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
});

// ══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════

const MiddlesDataGrid = ({ username, isAuthenticated = true }) => {
  const oddsFormat = useOddsFormat();
  const middles = useStoreData('middles');
  const betsData = useStoreData('odds');
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const { bookLogos, teamMap } = useLogoMaps();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // ── Build odds lookup map from betsData ──
  const oddsMap = useMemo(() => {
    const map = new Map();
    for (const bet of betsData) map.set(bet.id, bet);
    return map;
  }, [betsData]);

  // ── Enrich middle records with display data from odds ──
  const enrichedMiddles = useMemo(() => {
    if (!middles || !middles.length) return EMPTY_ARRAY;
    return middles.map(middle => {
      const bet1 = oddsMap.get(middle.odds_id_1);
      const bet2 = oddsMap.get(middle.odds_id_2);
      if (!bet1 || !bet2) return null;

      return {
        middle_key: middle.middle_key,

        // Event (from either bet — same fixture)
        eventName: `${teamMap[bet1.awayTeamId]?.abbreviation || teamMap[bet1.awayTeamId]?.name || '?'} @ ${teamMap[bet1.homeTeamId]?.abbreviation || teamMap[bet1.homeTeamId]?.name || '?'}`,
        eventStart: bet1.eventStart,
        league: bet1.league || 'NBA',
        homeTeam: teamMap[bet1.homeTeamId]?.name || '',
        awayTeam: teamMap[bet1.awayTeamId]?.name || '',
        homeLogo: teamMap[bet1.homeTeamId]?.logo || '',
        awayLogo: teamMap[bet1.awayTeamId]?.logo || '',
        homeAbbr: teamMap[bet1.homeTeamId]?.abbreviation || '',
        awayAbbr: teamMap[bet1.awayTeamId]?.abbreviation || '',

        // Bet info
        playerName: bet1.playerName,
        playerId: bet1.playerId || null,
        fixtureId: bet1.fixture_id || null,
        betTypeName: bet1.marketName || bet1.market || '',
        marketId: bet1.market_id || bet1.market || '',

        // Side 1
        bookName1: bet1.bookName,
        bookLogo1: bookLogos[bet1.bookName] || '',
        side1: capitalizeSide(middle.side_1),
        price1: middle.price_1,
        points1: middle.points_1,
        roi1: middle.roi_1,
        oddsId1: middle.odds_id_1,
        hasDeeplink1: bet1.hasDeeplink,

        // Side 2
        bookName2: bet2.bookName,
        bookLogo2: bookLogos[bet2.bookName] || '',
        side2: capitalizeSide(middle.side_2),
        price2: middle.price_2,
        points2: middle.points_2,
        roi2: middle.roi_2,
        oddsId2: middle.odds_id_2,
        hasDeeplink2: bet2.hasDeeplink,

        // Middle math
        lineGap: middle.line_gap,
        avgRoiPct: middle.avg_roi_pct,
      };
    }).filter(Boolean);
  }, [middles, oddsMap, bookLogos, teamMap]);

  // ── Available books from sportsbooks config ──
  const availableBooks = useMemo(() => {
    const orderIndex = {};
    PREFERRED_BOOK_ORDER.forEach((name, i) => { orderIndex[name] = i; });
    return sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean).sort((a, b) => {
      const ia = orderIndex[a] !== undefined ? orderIndex[a] : 9999;
      const ib = orderIndex[b] !== undefined ? orderIndex[b] : 9999;
      if (ia !== ib) return ia - ib;
      return a.localeCompare(b);
    });
  }, [sportsbooks]);

  // ── Saved filters ──
  const savedLeagueFilter = useMemo(() => { try { const s = localStorage.getItem('middleLeagueFilter'); return s ? JSON.parse(s) : []; } catch { return []; } }, []);
  const savedBookFilter = useMemo(() => { try { const s = localStorage.getItem('middleBookFilter'); return s ? JSON.parse(s) : []; } catch { return []; } }, []);
  const savedHideTracked = useMemo(() => { try { const s = localStorage.getItem('middleHideTracked'); return s ? JSON.parse(s) : true; } catch { return true; } }, []);
  const savedShowHiddenMiddles = useMemo(() => { try { const s = localStorage.getItem('middleShowHiddenMiddles'); return s ? JSON.parse(s) : false; } catch { return false; } }, []);

  const [leagueFilter, setLeagueFilter] = useState(savedLeagueFilter);
  const [bookFilter, setBookFilter] = useState(() => {
    // Validate saved books against availableBooks at init time to avoid post-mount setBookFilter calls
    try {
      const saved = JSON.parse(localStorage.getItem('middleBookFilter') || '[]');
      const books = sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean);
      if (!books.length) return saved;
      const valid = saved.filter(b => books.includes(b));
      if (valid.length >= 2) return valid;
      if (valid.length === 1) return [...valid, books.find(b => !valid.includes(b))].filter(Boolean);
      return books.slice(0, 2);
    } catch { return []; }
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [hideTracked, setHideTracked] = useState(savedHideTracked);
  const [showHiddenMiddles, setShowHiddenMiddles] = useState(savedShowHiddenMiddles);
  const [hiddenMiddles, setHiddenMiddles] = useState(() => {
    const saved = localStorage.getItem('hiddenMiddles');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.slice(-1000);
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  // ── Tracked Bets (internal state, backed by REST API) ──
  const [trackedBets, setTrackedBets] = useState([]);

  useEffect(() => {
    if (!username || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${TRACKING_API_URL}?username=${encodeURIComponent(username)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          // Normalize: add oddsId alias so existing checks work
          const bets = (data.tracked_bets || []).map(b => ({ ...b, oddsId: b.odds_id }));
          setTrackedBets(bets);
        }
      } catch (err) {
        console.error('[TRACKED-BETS] Failed to fetch:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [username, isAuthenticated]);

  const apiTrackBet = useCallback(async (row, side) => {
    const isFirst = side === 1;
    const oddsId = isFirst ? row.oddsId1 : row.oddsId2;
    const body = {
      username,
      odds_id:         oddsId,
      sportsbook:      isFirst ? row.bookName1 : row.bookName2,
      sportsbook_logo: isFirst ? row.bookLogo1 : row.bookLogo2,
      fixture_id:      row.fixtureId,
      event_name:      row.eventName,
      event_start:     row.eventStart,
      league:          row.league,
      player_name:     row.playerName,
      player_id:       row.playerId,
      market_id:       row.marketId,
      market_display:  row.betTypeName,
      side:            isFirst ? row.side1 : row.side2,
      points:          isFirst ? row.points1 : row.points2,
      price:           isFirst ? row.price1 : row.price2,
      roi:             (isFirst ? row.roi1 : row.roi2) / 100,
      home_team:       row.homeTeam || '',
      away_team:       row.awayTeam || '',
      home_logo:       row.homeLogo,
      away_logo:       row.awayLogo,
      source_type:     'middle',
      source_key:      row.middle_key,
    };
    const resp = await fetch(TRACKING_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok && resp.status !== 409) throw new Error(`Track failed: HTTP ${resp.status}`);
    // Optimistic update
    setTrackedBets(prev => [...prev, { ...body, oddsId: oddsId }]);
  }, [username]);

  const apiUntrackBet = useCallback(async (oddsId) => {
    const resp = await fetch(
      `${TRACKING_API_URL}/${encodeURIComponent(oddsId)}?username=${encodeURIComponent(username)}`,
      { method: 'DELETE' }
    );
    if (!resp.ok && resp.status !== 404) throw new Error(`Untrack failed: HTTP ${resp.status}`);
    setTrackedBets(prev => prev.filter(b => b.oddsId !== oddsId));
  }, [username]);

  const toggleTrack = useCallback(async (row, bet1Tracked, bet2Tracked) => {
    if (!isAuthenticated || !username) return;
    const middleKey = row.middle_key;
    if (trackingInProgress.has(middleKey)) return;
    setTrackingInProgress(prev => new Set(prev).add(middleKey));

    try {
      if (bet1Tracked && bet2Tracked) {
        // Untrack both legs
        const promises = [];
        if (row.oddsId1) promises.push(apiUntrackBet(row.oddsId1));
        if (row.oddsId2) promises.push(apiUntrackBet(row.oddsId2));
        await Promise.all(promises);
      } else {
        // Track missing legs
        const promises = [];
        if (!bet1Tracked && row.oddsId1) promises.push(apiTrackBet(row, 1));
        if (!bet2Tracked && row.oddsId2) promises.push(apiTrackBet(row, 2));
        await Promise.all(promises);
      }
    } catch (err) {
      console.error('[TRACK] Error:', err.message);
    } finally {
      setTrackingInProgress(prev => { const next = new Set(prev); next.delete(middleKey); return next; });
    }
  }, [isAuthenticated, username, apiTrackBet, apiUntrackBet, trackingInProgress]);

  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) return;
    const key = row.middle_key;
    const updated = hiddenMiddles.includes(key) ? hiddenMiddles.filter(x => x !== key) : [...hiddenMiddles, key];
    setHiddenMiddles(updated);
    localStorage.setItem('hiddenMiddles', JSON.stringify(updated));
  }, [hiddenMiddles, isAuthenticated]);

  useEffect(() => { localStorage.setItem('middleLeagueFilter', JSON.stringify(leagueFilter)); }, [leagueFilter]);
  useEffect(() => { localStorage.setItem('middleBookFilter', JSON.stringify(bookFilter)); }, [bookFilter]);
  useEffect(() => { localStorage.setItem('middleHideTracked', JSON.stringify(hideTracked)); }, [hideTracked]);
  useEffect(() => { localStorage.setItem('middleShowHiddenMiddles', JSON.stringify(showHiddenMiddles)); }, [showHiddenMiddles]);

  useEffect(() => {
    if (enrichedMiddles && enrichedMiddles.length > 0) {
      const currentKeys = new Set(enrichedMiddles.map(m => m.middle_key));
      setHiddenMiddles(prev => {
        const valid = prev.filter(key => currentKeys.has(key));
        return valid.length !== prev.length ? valid : prev;
      });
    }
  }, [enrichedMiddles]);

  const isMobile = useMediaQuery('(max-width: 768px)');
  useEffect(() => { const h = () => setScreenWidth(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  const handleSettingsOpen = useCallback((event) => { setSettingsAnchorEl(event.currentTarget); }, []);
  const handleSettingsClose = useCallback(() => { setSettingsAnchorEl(null); }, []);

  const availableLeagues = useMemo(() => {
    if (leaguesConfig.length > 0) {
      return leaguesConfig.map(l => l.name || l.id).filter(Boolean).sort();
    }
    const set = new Set();
    for (const m of enrichedMiddles) { if (m.league) set.add(m.league); }
    return Array.from(set).sort();
  }, [leaguesConfig, enrichedMiddles]);

  const { filterOptions, filteredMiddles } = useMemo(() => {
    const leagues = availableLeagues.length > 0 ? availableLeagues : ['NBA'];
    const books = availableBooks.length > 0 ? availableBooks : PREFERRED_BOOK_ORDER;
    if (!enrichedMiddles || !enrichedMiddles.length) return { filterOptions: { leagues, books: availableBooks }, filteredMiddles: [] };

    const leagueSet = new Set(leagueFilter);
    const bookSet = new Set(bookFilter);
    const trackedSet = new Set(trackedBets.map(bet => bet.oddsId || bet.odds_id));
    const hiddenSet = new Set(hiddenMiddles);

    const filtered = enrichedMiddles.filter((m) => {
      if (leagueFilter.length > 0 && m.league && !leagueSet.has(m.league)) return false;
      if (bookFilter.length > 0 && (!bookSet.has(m.bookName1) || !bookSet.has(m.bookName2))) return false;
      if (hideTracked && trackedSet.has(m.oddsId1) && trackedSet.has(m.oddsId2)) return false;
      if (!showHiddenMiddles && hiddenSet.has(m.middle_key)) return false;
      return true;
    });

    return { filterOptions: { leagues, books }, filteredMiddles: [...filtered].sort((a, b) => b.avgRoiPct - a.avgRoiPct) };
  }, [enrichedMiddles, leagueFilter, bookFilter, hideTracked, trackedBets, hiddenMiddles, showHiddenMiddles, availableLeagues, availableBooks]);

  const handleFilterChange = (e, options, setter, type) => {
    const val = e.target.value;
    if (type === 'book' && val.length < 2) {
      if (val.length === 0) setter(options.slice(0, 2));
      else if (val.length === 1) setter([...val, options.filter(opt => !val.includes(opt))[0]]);
      return;
    }
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const nv = prev.length === options.length ? [] : options;
        if (type === 'book' && nv.length < 2) return options.slice(0, 2);
        return nv;
      });
    } else { setter(val); }
  };

  const mobilePageSize = 5;
  const totalPages = Math.ceil(filteredMiddles.length / mobilePageSize);
  const paginatedMiddles = filteredMiddles.slice(currentPage * mobilePageSize, (currentPage + 1) * mobilePageSize);
  useEffect(() => { setCurrentPage(0); }, [leagueFilter, bookFilter, hideTracked, showHiddenMiddles]);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS
  // ══════════════════════════════════════════════════════════

  const columns = [
    {
      field: 'eventName',
      headerName: 'Event',
      minWidth: 165, flex: 2,
      headerAlign: 'left', align: 'left',
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')
          : '';
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {params.row.league && (<><Typography sx={{ fontSize: '14px', color: '#ffffff', mr: 0 }}>{params.row.league}</Typography><Typography sx={{ fontSize: '14px', color: '#ffffff', mx: 0.5 }}>|</Typography></>)}
              {params.row.awayLogo && <img src={params.row.awayLogo} alt="Away" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />}
              <Typography sx={{ fontSize: '14px' }}>{params.row.awayAbbr || params.row.awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {params.row.homeLogo && <img src={params.row.homeLogo} alt="Home" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />}
              <Typography sx={{ fontSize: '14px' }}>{params.row.homeAbbr || params.row.homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165, flex: 2,
      headerAlign: 'left', align: 'left',
      renderCell: (params) => (
        <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title={params.row.playerName || ''} placement="top" arrow>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
              {params.row.playerName || ''}
            </Typography>
          </Tooltip>
          <Tooltip title={params.row.betTypeName || ''} placement="top" arrow>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
              {params.row.betTypeName || ''}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'bookLogo1',
      headerName: '',
      minWidth: 26, maxWidth: 26,
      sortable: false, headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '50%', padding: 0 }}>
            <Tooltip title={params.row.bookName1 || ''} placement="top" arrow>
              <img src={params.value} alt="Logo 1" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" />
            </Tooltip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '50%', padding: 0 }}>
            <Tooltip title={params.row.bookName2 || ''} placement="bottom" arrow>
              <img src={params.row.bookLogo2} alt="Logo 2" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" />
            </Tooltip>
          </div>
        </Box>
      ),
    },
    {
      field: 'lineOdds',
      headerName: 'Line/Odds',
      minWidth: 130, flex: 1,
      sortable: false, headerAlign: 'left', align: 'right',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', paddingRight: '8px' }}>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>{formatLine(params.row.side1, params.row.points1)}</Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>{fmtPrice(params.row.price1, oddsFormat)}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>{formatLine(params.row.side2, params.row.points2)}</Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>{fmtPrice(params.row.price2, oddsFormat)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'ev',
      headerName: 'EV',
      minWidth: 100, flex: 1,
      sortable: false, headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', paddingLeft: '8px' }}>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Typography variant="body2" sx={{ color: '#00ff00' }}>{params.row.roi1.toFixed(1)}%</Typography>
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Typography variant="body2" sx={{ color: '#00ff00' }}>{params.row.roi2.toFixed(1)}%</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'trackMiddle', headerName: 'Track', sortable: false, minWidth: 65, flex: 1, headerAlign: 'left', align: 'left',
      renderCell: params => <TrackMiddleCell row={params.row} trackedBets={trackedBets} toggleTrack={toggleTrack} trackingInProgress={trackingInProgress} isAuthenticated={isAuthenticated} />
    },
    {
      field: 'hideMiddle', headerName: 'Hide', sortable: false, minWidth: 65, flex: 1, headerAlign: 'left', align: 'left',
      renderCell: params => <HideMiddleCell row={params.row} hiddenMiddles={hiddenMiddles} toggleHide={toggleHide} showHiddenMiddles={showHiddenMiddles} isAuthenticated={isAuthenticated} />
    },
    {
      field: 'deeplink1', headerName: '', sortable: false, minWidth: 80, flex: 1, headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell oddsId={params.row.oddsId1} hasDeeplink={params.row.hasDeeplink1} isAuthenticated={isAuthenticated} />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell oddsId={params.row.oddsId2} hasDeeplink={params.row.hasDeeplink2} isAuthenticated={isAuthenticated} />
          </Box>
        </Box>
      ),
    },
  ];

  const columnVisibilityModel = {};

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {isMobile ? (
            <>
              <Box sx={{ position: 'sticky', top: 0, backgroundColor: '#121212', px: 2, py: 1.5, borderBottom: '1px solid #333', zIndex: 10 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="m-league" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select multiple labelId="m-league" label="League" notched value={leagueFilter} onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')} renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')} sx={{ fontSize: '0.875rem' }} disabled={!isAuthenticated}>
                        <MenuItem value="all"><Checkbox checked={leagueFilter.length === filterOptions.leagues.length} /><ListItemText primary="Select All" /></MenuItem>
                        {filterOptions.leagues.map(o => <MenuItem key={o} value={o}><Checkbox checked={leagueFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="m-book" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select multiple labelId="m-book" label="Book" notched value={bookFilter} onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')} renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')} sx={{ fontSize: '0.875rem' }} disabled={!isAuthenticated}>
                        <MenuItem value="all"><Checkbox checked={bookFilter.length === filterOptions.books.length} /><ListItemText primary="Select All" /></MenuItem>
                        {filterOptions.books.map(o => <MenuItem key={o} value={o}><Checkbox checked={bookFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
                      </Select>
                    </FormControl>
                    <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
                      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, paddingBottom: totalPages > 1 ? '80px' : '16px' }}>
                {filteredMiddles.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>No middle opportunities available</Typography>
                  </Box>
                ) : (
                  paginatedMiddles.map(middle => (
                    <MobileMiddleCard key={middle.middle_key} middle={middle} trackedBets={trackedBets} toggleTrack={toggleTrack} trackingInProgress={trackingInProgress} isAuthenticated={isAuthenticated} hiddenMiddles={hiddenMiddles} toggleHide={toggleHide} showHiddenMiddles={showHiddenMiddles} />
                  ))
                )}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#1e1e1e', borderTop: '1px solid #333', p: 2, zIndex: 9 }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button size="small" onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0 || !isAuthenticated} sx={{ color: '#ffffff' }}>Previous</Button>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>{currentPage + 1} / {totalPages}</Typography>
                    <Button size="small" onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1 || !isAuthenticated} sx={{ color: '#ffffff' }}>Next</Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box className="filter-section">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <FilterSelect id="league-filter-label" label="League" value={leagueFilter} options={filterOptions.leagues} onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')} disabled={!isAuthenticated} />
                    <FilterSelect id="book-filter-label" label="Book" value={bookFilter} options={filterOptions.books} onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')} disabled={!isAuthenticated} />
                  </Stack>
                  <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
                    <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
              </Box>
              <Box className="table-section">
                <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  {filteredMiddles.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>No middle opportunities available</Typography>
                    </Box>
                  ) : (
                    <DataGridPro
                      rows={filteredMiddles}
                      columns={columns}
                      getRowId={row => row.middle_key}
                      sortingMode="client"
                      disableSelectionOnClick
                      disableColumnMenu
                      hideFooter
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e1e1e', borderBottom: '2px solid #444', position: 'sticky', top: '56px', zIndex: 2 },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': { color: '#ffffff !important', userSelect: 'none', borderRight: '1px solid rgba(255, 255, 255, 0.3)' },
                        '& .MuiDataGrid-cell[data-field="bookLogo1"]': { padding: 0 },
                        '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row': { position: 'relative', '&:hover': { backgroundColor: '#1a1a1a' } },
                        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                        '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: '#ffffff !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 },
                        '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0, boxSizing: 'border-box' },
                        '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
                        '& .MuiDataGrid-main': { padding: 0, boxSizing: 'border-box' },
                        '& .MuiDataGrid-footerContainer': { backgroundColor: '#1e1e1e', color: '#ffffff !important', position: 'sticky', bottom: 0, zIndex: 1, boxSizing: 'border-box' },
                      }}
                      rowBuffer={5}
                      rowThreshold={2}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

          <Popover open={Boolean(settingsAnchorEl)} anchorEl={settingsAnchorEl} onClose={handleSettingsClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ zIndex: 1300 }}>
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Stack direction="column" spacing={1}>
                <FormControlLabel control={<Checkbox checked={hideTracked} onChange={(e) => setHideTracked(e.target.checked)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} disabled={!isAuthenticated} />} label="Hide Tracked Bets" sx={{ color: '#ffffff' }} />
                <FormControlLabel control={<Checkbox checked={showHiddenMiddles} onChange={(e) => setShowHiddenMiddles(e.target.checked)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} disabled={!isAuthenticated} />} label="Show Hidden Middles" sx={{ color: '#ffffff', mb: 2 }} />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={handleSettingsClose} sx={{ color: '#ffffff' }}>Cancel</Button>
                <Button size="small" onClick={handleSettingsClose} variant="contained" sx={{ backgroundColor: '#ff1744', '&:hover': { backgroundColor: '#d81b60' } }}>Save</Button>
              </Stack>
            </Box>
          </Popover>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default MiddlesDataGrid;